/**
 * logEvent — fire-and-forget product analytics helper.
 *
 * Writes to our own /api/events/log (server-side, funnel-friendly) AND to PostHog
 * when available (for replays, heatmaps, dashboards). Never throws; analytics
 * must not break the UI.
 *
 * Browser event types live in CLIENT_EVENTS on the backend. Successful service
 * operations are logged by the server. Optional browser analytics needs consent.
 */
import axios from 'axios';
import { hasAnalyticsConsent, trackEvent } from '@/utils/analytics';

const backend = process.env.REACT_APP_BACKEND_URL?.replace(/\/$/, '');
const API = backend ? `${backend}/api` : '/api';

// Operational events below are emitted by their successful server actions.
// Keeping them server-owned avoids duplicate or fabricated purchase/export data.
const SERVER_EVENTS = new Set(['signup', 'email_verified', 'resume_created', 'resume_saved',
  'resume_exported', 'first_resume_exported', 'payment_initialized', 'payment_succeeded',
  'first_paid', 'magic_resume_complete', 'ai_enhance_used']);

export function logEvent(eventType, metadata = {}, { external = true } = {}) {
  if (SERVER_EVENTS.has(eventType)) return;
  // Browser journey analytics follow the existing optional-analytics choice.
  if (!hasAnalyticsConsent()) return;
  if (external) {
    try { trackEvent(eventType, metadata); } catch { /* non-fatal analytics failure */ }
  }
  // PostHog (client-side) — cheap, fire first so it lands even if our API is slow
  try {
    if (external && typeof window !== 'undefined' && window.posthog?.capture) {
      window.posthog.capture(eventType, metadata);
    }
  } catch {
    /* noop */
  }

  // Our own event stream (for server-side funnels + idempotent "first_*" events)
  try {
    axios
      .post(
        `${API}/events/log`,
        { event_type: eventType, metadata },
        { withCredentials: true, timeout: 5000 }
      )
      .catch(() => {
        /* analytics must never break UX */
      });
  } catch {
    /* noop */
  }
}
